import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import type { Ticket } from '../types/triage';

interface TriageContextValue {
  // Selection
  selectedLocationId: string | null;
  selectedTicketId: number | null;

  // Actions
  selectLocation: (id: string | null) => void;
  selectTicket: (id: number | null) => void;

  // Ticket list reference for auto-advance
  ticketListRef: React.MutableRefObject<Ticket[]>;
  advanceToNextTicket: () => void;

  // Mutation feedback
  lastActionResult: { ticketId: number; action: string; timestamp: Date } | null;
  setLastActionResult: (result: { ticketId: number; action: string; timestamp: Date } | null) => void;
}

// Exported so callers can read the context directly with React.useContext when
// they want a no-throw fallback (LocationPane is shared by /tasks which lives
// outside TriageProvider — useTriage() would throw, but useContext returns null).
export const TriageContext = createContext<TriageContextValue | null>(null);

export const TriageProvider: React.FC<{
  children: React.ReactNode;
  onSelectLocation: (id: string | null) => void;
  onSelectTicket: (id: number | null) => void;
}> = ({ children, onSelectLocation, onSelectTicket }) => {
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);
  const [lastActionResult, setLastActionResult] = useState<TriageContextValue['lastActionResult']>(null);
  const ticketListRef = useRef<Ticket[]>([]);

  const selectLocation = useCallback((id: string | null) => {
    setSelectedLocationId(id);
    onSelectLocation(id);
    setSelectedTicketId(null);
  }, [onSelectLocation]);

  const selectTicket = useCallback((id: number | null) => {
    setSelectedTicketId(id);
    onSelectTicket(id);
  }, [onSelectTicket]);

  const advanceToNextTicket = useCallback(() => {
    const list = ticketListRef.current;
    if (!selectedTicketId || list.length === 0) return;
    const idx = list.findIndex((t) => t.ticket_id === selectedTicketId);
    const nextIdx = idx + 1 < list.length ? idx + 1 : 0;
    selectTicket(list[nextIdx]?.ticket_id ?? null);
  }, [selectedTicketId, selectTicket]);

  return (
    <TriageContext.Provider
      value={{
        selectedLocationId,
        selectedTicketId,
        selectLocation,
        selectTicket,
        ticketListRef,
        advanceToNextTicket,
        lastActionResult,
        setLastActionResult,
      }}
    >
      {children}
    </TriageContext.Provider>
  );
};

export function useTriage() {
  const ctx = useContext(TriageContext);
  if (!ctx) throw new Error('useTriage must be used within TriageProvider');
  return ctx;
}
